import React from "react";
import {
  Routes,
  Route,
  Navigate,
  Outlet,
  useParams,
  useLocation,
} from "react-router-dom";
import { ContentProvider, CmsAutoRefreshInitializer } from "./cms";
import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import Header from "./components/Header";
import Footer from "./components/Footer";
import ScrollToHero from "./components/ScrollToHero";
import About from "./components/About";
import Packages from "./components/Packages";
import Gallery from "./components/Gallery";
import Contact from "./components/Contact";
import BookExperience from "./components/BookExperience";
import WhatsAppButton from "./components/WhatsAppButton";
import CookieBanner from "./components/CookieBanner";
import CookieSettingsModal from "./components/CookieSettingsModal";
import CmsBridge from "./components/CmsBridge";
import LaplandHolidayPackages from "./pages/en/LaplandHolidayPackages";
import HuskyRide from "./pages/en/Husky-Ride";
import SnowmobileSafariEn from "./pages/en/Snowmobile-Safari";
import NorthernLights from "./pages/en/Northern-Lights";
import Accommodation from "./pages/en/Accommodation";
import AccommodationDe from "./pages/de/Accommodation";
import AccommodationPl from "./pages/pl/Accommodation";
import Hundspann from "./pages/sv/Hundspann";
import Skotersafari from "./pages/sv/Skotersafari";
import Norrsken from "./pages/sv/Norrsken";
import Boende from "./pages/sv/Boende";
import HuskyTour from "./pages/de/Husky-Tour";
import SchneemobilSafari from "./pages/de/Schneemobil-Safari";
import Nordlichter from "./pages/de/Nordlicht-Tour";
import HomePage from "./pages/shared/HomePage";
import PolicyPage from "./pages/shared/PolicyPage";
import FaqPage from "./pages/shared/FaqPage";
import ConsentAwareIntegrations from "./components/ConsentAwareIntegrations";
import { SUPPORTED_LANGUAGES } from "./utils/routes";
import { sparBesok } from "./utils/spar";

const LocalizedRoute = ({ children }) => {
  const { lang } = useParams();
  const location = useLocation();
  const { i18n } = useTranslation();
  const normalized = SUPPORTED_LANGUAGES.includes(lang || "") ? lang || "en" : "en";

  useEffect(() => {
    if (i18n.language !== normalized) {
      i18n.changeLanguage(normalized);
    }
  }, [normalized, i18n]);

  // Sidvisning till SCC vid varje ruttbyte, med språket besökaren landat på.
  useEffect(() => {
    sparBesok(normalized);
  }, [location.pathname, normalized]);

  if (!SUPPORTED_LANGUAGES.includes(lang || "")) {
    const suffix = location.pathname.replace(/^\/[^/]+/, "");
    return <Navigate to={`/${normalized}${suffix || "/"}`} replace />;
  }

  return children;
};

const LanguageLayout = () => (
  <LocalizedRoute>
    <Outlet />
  </LocalizedRoute>
);

function App() {
  const { i18n } = useTranslation();
  const currentLang = SUPPORTED_LANGUAGES.includes(i18n.language)
    ? i18n.language
    : "en";

  return (
    <ContentProvider>
      <CmsAutoRefreshInitializer />
      <div className="min-h-screen bg-slate-900 text-white">
        <ConsentAwareIntegrations />
        <Header />
        <ScrollToHero />
        <main>
          <Routes>
            <Route path="/" element={<Navigate to={`/${currentLang}/`} replace />} />

            <Route path=":lang" element={<LanguageLayout />}>
              <Route index element={<HomePage />} />
              <Route path="about" element={<About />} />
              <Route path="packages" element={<Packages />} />
              <Route path="gallery" element={<Gallery />} />
              <Route path="contact" element={<Contact />} />
              <Route path="faq" element={<FaqPage />} />
              <Route path="book" element={<BookExperience />} />
              <Route path="privacy" element={<PolicyPage policyKey="privacy" />} />
              <Route path="terms" element={<PolicyPage policyKey="terms" />} />
              <Route path="cookies" element={<PolicyPage policyKey="cookies" />} />

              <Route path="lapland-holiday-packages" element={<LaplandHolidayPackages />} />
              <Route path="pakiety-laponii" element={<LaplandHolidayPackages />} />
              <Route path="husky-ride" element={<HuskyRide />} />
              <Route path="snowmobile-safari" element={<SnowmobileSafariEn />} />
              <Route path="northern-lights" element={<NorthernLights />} />
              <Route path="accommodation" element={
                currentLang === "pl"
                  ? <AccommodationPl />
                  : currentLang === "de"
                    ? <AccommodationDe />
                    : <Accommodation />
              } />

              <Route path="paketresor" element={<LaplandHolidayPackages />} />
              <Route path="hundspann" element={<Hundspann />} />
              <Route path="skotersafari" element={<Skotersafari />} />
              <Route path="norrsken" element={<Norrsken />} />
              <Route path="boende" element={<Boende />} />

              <Route path="lappland-reisepakete" element={<LaplandHolidayPackages />} />
              <Route path="husky-tour" element={<HuskyTour />} />
              <Route path="schneemobil-safari" element={<SchneemobilSafari />} />
              <Route path="nordlichter" element={<Nordlichter />} />
              <Route path="accommodation" element={<Accommodation />} />
            </Route>

            <Route path="about" element={<Navigate to={`/${currentLang}/about`} replace />} />
            <Route path="packages" element={<Navigate to={`/${currentLang}/packages`} replace />} />
            <Route path="gallery" element={<Navigate to={`/${currentLang}/gallery`} replace />} />
            <Route path="contact" element={<Navigate to={`/${currentLang}/contact`} replace />} />
            <Route path="book" element={<Navigate to={`/${currentLang}/book`} replace />} />
            <Route path="faq" element={<Navigate to={`/${currentLang}/faq`} replace />} />
            <Route path="privacy" element={<Navigate to={`/${currentLang}/privacy`} replace />} />
            <Route path="terms" element={<Navigate to={`/${currentLang}/terms`} replace />} />
            <Route path="cookies" element={<Navigate to={`/${currentLang}/cookies`} replace />} />

            <Route path="/en/husky-tour" element={<Navigate to="/en/husky-ride" replace />} />
            <Route path="/en/dog-sled" element={<Navigate to="/en/husky-ride" replace />} />
            <Route path="/en/dogsled" element={<Navigate to="/en/husky-ride" replace />} />
            <Route path="/en/dog-sledding" element={<Navigate to="/en/husky-ride" replace />} />
            <Route path="/sv/hundslade" element={<Navigate to="/sv/hundspann" replace />} />
            <Route path="/sv/hundsläde" element={<Navigate to="/sv/hundspann" replace />} />
            <Route path="/de/hundeschlitten" element={<Navigate to="/de/husky-tour" replace />} />
            <Route path="/de/schlittenhunde" element={<Navigate to="/de/husky-tour" replace />} />
            <Route path="/de/schneemobil-tour" element={<Navigate to="/de/schneemobil-safari" replace />} />
            <Route path="/en/packages" element={<Navigate to="/en/lapland-holiday-packages" replace />} />
            <Route path="/sv/packages" element={<Navigate to="/sv/paketresor" replace />} />
            <Route path="/de/packages" element={<Navigate to="/de/lappland-reisepakete" replace />} />
            <Route path="/pl/packages" element={<Navigate to="/pl/pakiety-laponii" replace />} />
            <Route path="/pl/lapland-holiday-packages" element={<Navigate to="/pl/pakiety-laponii" replace />} />

            <Route path="*" element={<Navigate to={`/${currentLang}/`} replace />} />
          </Routes>
        </main>
        <Footer />
        <CookieSettingsModal />
        <CookieBanner />
        <WhatsAppButton />
        <CmsBridge />
      </div>
    </ContentProvider>
  );
}

export default App;
